import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Password } from 'primereact/password';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { useTenant } from '../contexts/TenantContext';
import axios from '../config/axios.config';
import Swal from 'sweetalert2';

const ResetPassword = () => {
    // Keep all existing state variables
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);
    const [errors, setErrors] = useState({});
    
    const { token } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);
    const { tenantIdentifier } = useTenant();
    
    // Get email from query parameters
    const queryParams = new URLSearchParams(location.search);
    const email = queryParams.get('email') || '';

    // Keep all existing useEffect and functions
    useEffect(() => {
        // Optionally validate token on mount
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            // You can add an endpoint to validate the token
            // await axios.post('/tenant/validate-reset-token', { token });
            setTokenValid(true);
        } catch (err) {
            setTokenValid(false);
            setError('Invalid or expired reset token');
        }
    };

    const validate = () => {
        const newErrors = {};
        
        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }
        
        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setSubmitted(true);
        setError('');
        
        if (!validate()) {
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post('/tenant/reset-password', {
                token,
                email,
                password,
                password_confirmation: confirmPassword,
                tenant: tenantIdentifier
            });

            setSuccess(true);
            
            // Show SweetAlert success message
            Swal.fire({
                title: 'Password Reset Successful!',
                text: 'Your password has been updated successfully',
                icon: 'success',
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false
            });

            // Close SweetAlert and navigate after short delay
            setTimeout(() => {
                Swal.close();
                navigate('/app/login');
            }, 1800);
        } catch (err) {
            const message = err.response?.data?.message || 'Failed to reset password. Please try again.';
            setError(message);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
        
    };

    const handleCancel = () => {
        navigate('/app/login');
    };

    // Invalid token screen - Consistent with register/login design
    if (!tokenValid) {
        return (
            <>
                <Toast ref={toast} />
                <div className="flex min-h-screen">
                    {/* Left Panel - Form Section */}
                    <div className="w-full lg:w-4 min-h-screen lg:h-screen text-center px-4 py-4 lg:px-6 lg:py-6 flex flex-column justify-content-center lg:justify-content-between surface-ground overflow-y-auto">
                        {/* Logo - Hidden on mobile, shown on desktop */}
                        <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="mx-auto hidden lg:block" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                        <div className="flex flex-column align-items-center gap-3 lg:gap-4">
                            {/* Logo - Shown on mobile only */}
                            <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="block lg:hidden mb-3" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                            <div className="mb-2 lg:mb-3 text-center">
                                <i className="pi pi-exclamation-triangle text-orange-500 text-5xl lg:text-6xl mb-3"></i>
                                <h2 className="text-xl lg:text-2xl">Invalid Reset Link</h2>
                                <p>This password reset link is invalid or has expired</p>
                            </div>

                            <div className="flex flex-column gap-3 lg:gap-4 w-full max-w-25rem">
                                <Button
                                    label="REQUEST NEW RESET LINK"
                                    icon="pi pi-refresh"
                                    className="w-full"
                                    onClick={() => navigate('/app/forgot-password')}
                                />

                                <Button
                                    label="BACK TO LOGIN"
                                    outlined
                                    className="w-full"
                                    onClick={() => navigate('/app/login')}
                                />

                                <span className="font-semibold text-color-secondary">
                                    Need help? <a className="text-primary hover:underline cursor-pointer font-medium">Contact support</a>
                                </span>
                            </div>
                        </div>

                    </div>

                    {/* Right Panel - Hero Section */}
                    <div className="w-8 hidden lg:flex flex-column justify-content-center align-items-center px-6 py-6 auth-hero-section">
                        <div className="text-center">
                            <span className="block text-white text-7xl font-bold mb-4">
                                Security
                            </span>
                            <span className="block text-white text-5xl font-semibold mb-4">
                                Alert
                            </span>
                            <span className="block text-white text-xl line-height-3">
                                Your link has expired or is invalid.
                                <br />Please request a new password
                                <br />reset link for security.
                            </span>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Main reset password form - Consistent with register/login design
    return (
        <>
            <Toast ref={toast} />
            <div className="flex min-h-screen">
                {/* Left Panel - Form Section */}
                <div className="w-full lg:w-4 min-h-screen lg:h-screen text-center px-4 py-4 lg:px-6 lg:py-6 flex flex-column justify-content-center lg:justify-content-between surface-ground overflow-y-auto">
                    {/* Logo - Hidden on mobile, shown on desktop */}
                    <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="mx-auto hidden lg:block" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                    <div className="flex flex-column align-items-center gap-3 lg:gap-4">
                        {/* Logo - Shown on mobile only */}
                        <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="block lg:hidden mb-3" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                        <div className="mb-2 lg:mb-3 text-center">
                            <h2 className="text-xl lg:text-2xl">Reset Password</h2>
                            <p>Create a new password for your account</p>
                        </div>

                        <div className="flex flex-column gap-3 lg:gap-4 w-full max-w-25rem">
                            {/* Error Message */}
                            {error && !success && (
                                <small className="p-error text-left">{error}</small>
                            )}
                            
                            {/* Success Message */}
                            {success && (
                                <div className="flex align-items-center justify-content-center p-3 mb-3 border-round bg-green-50 text-green-800 border-1 border-green-200">
                                    <i className="pi pi-check-circle text-2xl mr-3 text-green-600"></i>
                                    <div className="text-center">
                                        <div className="font-semibold text-lg mb-1">Password Reset Successful!</div>
                                        <small className="text-green-700">Your password has been updated. Redirecting to login...</small>
                                    </div>
                                </div>
                            )}

                            {/* Password Fields */}
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-lock z-2"></i>
                                <Password 
                                    id="password"
                                    className={classNames('w-full', { 'p-invalid': errors.password && submitted })}
                                    inputClassName="w-full"
                                    inputStyle={{paddingLeft: '2.5rem'}}
                                    placeholder="New Password" 
                                    toggleMask
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={loading || success}
                                    feedback={false}
                                />
                            </span>
                            {errors.password && submitted && (
                                <small className="p-error text-left">{errors.password}</small>
                            )}
                            
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-lock z-2"></i>
                                <Password 
                                    id="confirmPassword"
                                    className={classNames('w-full', { 'p-invalid': errors.confirmPassword && submitted })}
                                    inputClassName="w-full"
                                    inputStyle={{paddingLeft: '2.5rem'}}
                                    placeholder="Confirm Password" 
                                    toggleMask
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={loading || success}
                                    feedback={false}
                                />
                            </span>
                            {errors.confirmPassword && submitted && (
                                <small className="p-error text-left">{errors.confirmPassword}</small>
                            )}

                            {/* Password Match Indicator */}
                            {password && confirmPassword && (
                                <div className="mt-1">
                                    {password === confirmPassword ? (
                                        <small className="flex align-items-center gap-2 text-green-600">
                                            <i className="pi pi-check-circle text-sm"></i>
                                            <span className="text-sm">Passwords match</span>
                                        </small>
                                    ) : (
                                        <small className="flex align-items-center gap-2 text-red-500">
                                            <i className="pi pi-times-circle text-sm"></i>
                                            <span className="text-sm">Passwords do not match</span>
                                        </small>
                                    )}
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="flex gap-3 w-full">
                                <Button 
                                    outlined 
                                    className="flex-auto"
                                    onClick={handleCancel}
                                    label="CANCEL"
                                    disabled={loading || success}
                                />
                                <Button 
                                    className="flex-auto"
                                    onClick={handleSubmit}
                                    label={loading ? 'RESETTING...' : (success ? 'SUCCESS!' : 'RESET PASSWORD')}
                                    loading={loading}
                                    disabled={loading || success}
                                    icon={success ? 'pi pi-check' : null}
                                />
                            </div>

                            {/* Additional Links */}
                            <span className="font-semibold text-color-secondary">
                                Remember your password? <Link to="/app/login" className="font-semibold cursor-pointer primary-color">Sign in</Link>
                            </span>
                        </div>
                    </div>

                </div>

                {/* Right Panel - Hero Section */}
                <div className="w-8 hidden lg:flex flex-column justify-content-center align-items-center px-6 py-6 auth-hero-section">
                    <div className="text-center">
                        <span className="block text-white text-7xl font-bold mb-4">
                            Reset
                        </span>
                        <span className="block text-white text-5xl font-semibold mb-4">
                            Password
                        </span>
                        <span className="block text-white text-xl line-height-3">
                            Create a strong password to protect
                            <br />your account. Use a combination of
                            <br />letters, numbers, and symbols.
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;